import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect } from 'react';

import InlineTooltip from 'widgets-v5/inline-tooltip';
import { SimpleSelect } from 'widgets-v6/select';

import { isInternalUser } from 'states/profile/business-rules';
import {
    getCurrentFlight,
    getMostRecentFlight,
    isCampaignLegacy,
    isCampaignLockedOrArchived,
} from 'states/resources/campaigns/business-logic';
import GlobalRefresh from 'containers/global-refresh';
import { can } from 'services/can';
import CampaignSharing from 'containers/campaign-sharing';
import flags from 'containers/flags/service';
import store from 'core/store';
import { PermissionsMapping } from 'common/constants/role-intents';
import c from 'common/constants/flux-events';
import {
    canShowNewReportingSwitch,
    clearUseNewReportTablePageOverride,
    getUseNewReportTable,
    NewReportingSwitchRow,
    PERSIST_HINT_CAMPAIGN,
    updateUseNewReportTablePreference,
} from 'containers/new-reporting-switch';
import { actions as adsOverviewV2Actions } from 'pages/campaigns/ads/ads-overview/v2';
import { fetchAdDeliveryData } from 'pages/campaigns/progress/ad-progress/progress-graph/actions';
import { fetchCampaignDeliveryData } from 'pages/campaigns/progress/campaign-progress/actions';
import Ads from 'states/resources/ads/actions';

import FlightIndicator from 'widgets-v5/flight-indicator';
import WorkspaceTopBar from 'widgets-v5/workspace-top-bar';
import { ButtonGroup } from 'widgets-v5/buttons';
import {
    CampaignIndicator,
    CampaignIndicatorWithAlert,
} from 'widgets-v6/lifecycle-and-health-indicator';
import { useCampaignPageContainer } from '../../v2';

const OVERVIEW_MENU = [
    {
        value: 'setup',
        label: 'Setup',
        link: campaignId => `/campaigns/${campaignId}/setup`,
        match: /campaigns\/.*\/setup.*/,
        requiresIntent: 'campaign__view',
        requiresCampaignAds: false,
        requiresBetaEnv: false,
    },
    {
        value: 'progress',
        label: 'Real-time Progress',
        link: campaignId => `/campaigns/${campaignId}/progress`,
        match: /campaigns\/.*\/progress.*/,
        requiresIntent: 'progress__view',
        requiresCampaignAds: true,
        requiresBetaEnv: false,
    },
    {
        value: 'analytics',
        label: 'Report',
        link: campaignId => `/campaigns/${campaignId}/report`,
        match: /campaigns\/.*\/report.*/,
        requiresIntent: 'campaign__view',
        requiresCampaignAds: true,
        requiresBetaEnv: false,
    },
    {
        value: 'client-report',
        label: 'Client Report',
        link: campaignId => `/campaigns/${campaignId}/client-report`,
        match: /campaigns\/.*\/client-report.*/,
        requiresIntent: 'report__view',
        requiresCampaignAds: true,
        requiresBetaEnv: false,
    },
];

function getMenuItems() {
    return OVERVIEW_MENU;
}

export default function CampaignHeader(props) {
    const { campaign } = props;
    const dispatch = useDispatch();
    const useNewReportTable = useSelector(getUseNewReportTable);
    const campaignPageContainer = useCampaignPageContainer();
    const selectedTab = getMenuItems().find(
        menuItem => menuItem.match && menuItem.match.test(window.location.pathname)
    );
    const selectedTabValue = selectedTab ? selectedTab.value : null;

    const visibleTabs = getMenuItems()
        .filter(menuItem => !menuItem.requiresIntent || can(menuItem.requiresIntent))
        // Eventually instead of hiding the tabs that require campaign ads, show them but grey them out
        .filter(menuItem => !menuItem.requiresCampaignAds || campaign.ads.length > 0);

    const tabs = visibleTabs.map(({ label, value, link, href }) => {
        let disableTab;
        let tooltip;
        if (value === 'progress') {
            disableTab = isCampaignLockedOrArchived(campaign);
            tooltip = 'This tab is not available for finalized/archived campaigns';
        } else {
            disableTab =
                isCampaignLegacy(campaign) && ['analytics', 'client-report'].includes(value);
            tooltip = disableTab ? 'This tab is not available for legacy campaigns' : null;
        }

        return {
            label,
            value,
            link: link && link(campaign.id),
            href: href && href(campaign.id),
            disabled: disableTab,
            tooltip: tooltip,
        };
    });

    const flightOptions = [
        {
            value: 'campaign',
            label: 'Entire Campaign View',
        },
    ];
    if (campaignPageContainer.campaign) {
        const currentFlight = getCurrentFlight(campaignPageContainer.campaign);
        const mostRecentFlight = getMostRecentFlight(campaignPageContainer.campaign);

        _.each(campaignPageContainer.campaign.flights, flight => {
            let label = flight.name;
            if (currentFlight && currentFlight.id === flight.id) {
                label += ' (current)';
            }
            if (!currentFlight && mostRecentFlight && mostRecentFlight.id === flight.id) {
                label += ' (most recent)';
            }

            flightOptions.push({
                value: flight.id,
                label,
            });
        });
    }

    const newLifecycleAndHealthIndicator = flags.isEnabled(
        'efb_390_new_lifecycle_and_health_statuses_label'
    );
    const isShowingAlert =
        campaign.lifecycle.current_state === 'finalized' ||
        campaign.lifecycle.current_state === 'archived' ||
        campaign.lifecycle.current_state === 'locked' ||
        campaign.lifecycle.current_state === 'legacy';

    // [EFB-456] / [EFB-1292] /report and /client-report refetch POST /report.
    // /setup: Ads.getAll sends useNewReportTable. /progress refetches delivery.
    const refreshActiveCampaignTab = () => {
        const pathname = window.location.pathname;
        const campaignId = campaign.id;

        if (/\/(report|client-report)/.test(pathname)) {
            dispatch({
                type: c.CAMPAIGN_REPORT__NEW_REPORTING_PREFERENCE__CHANGED,
                payload: { campaignId },
            });
        }

        if (/\/setup/.test(pathname)) {
            dispatch(adsOverviewV2Actions.refresh());
            dispatch(Ads.getAll(campaignId));
        }

        if (/\/progress/.test(pathname)) {
            const adMatch = pathname.match(/\/progress\/(\d+)/);
            const timezone = campaign.default_timezone;

            if (adMatch) {
                return dispatch(fetchAdDeliveryData(campaignId, adMatch[1], timezone));
            }

            return dispatch(fetchCampaignDeliveryData(campaignId, null, timezone));
        }

        return undefined;
    };

    const toggleUseNewReportTable = async enabled => {
        await dispatch(updateUseNewReportTablePreference(enabled));
        await refreshActiveCampaignTab();
    };

    useEffect(() => {
        return () => {
            dispatch(clearUseNewReportTablePageOverride());
        };
    }, [dispatch]);

    useEffect(() => {
        const override = store.getState()?.profile?.useNewReportTablePageOverride;

        if (override !== true && override !== false) {
            return;
        }

        // New tab already fetched in componentDidMount. Clear, then refetch.
        dispatch(clearUseNewReportTablePageOverride());
        refreshActiveCampaignTab();
    }, [dispatch, selectedTabValue]);

    // Flight dropdown only for campaign-pacing campaigns that support view-by.
    const showFlightSelect =
        _.get(campaignPageContainer, 'campaign.flightPacingStrategy') === 'campaign' &&
        _.get(campaignPageContainer, 'campaign.viewBy') !== null;
    // Skip an empty second row when neither the switch nor the flight picker is shown.
    const showLine2Content = canShowNewReportingSwitch(useNewReportTable) || showFlightSelect;

    return (
        <WorkspaceTopBar
            className="ef5-campaign-overview-header"
            title={`#${campaign.id} ${campaign.name}`}
            // Switch on the left, flight picker on the right — same row, below the title.
            line2Content={
                showLine2Content ? (
                    <div className="ef5-campaign-overview-header__controls-row">
                        <NewReportingSwitchRow
                            persistHint={PERSIST_HINT_CAMPAIGN}
                            checked={useNewReportTable}
                            onChange={toggleUseNewReportTable}
                        />
                        {showFlightSelect && (
                            // margin-left: auto keeps this on the right if the switch is hidden.
                            <div className="ef5-campaign-overview-header__flight-select">
                                <SimpleSelect
                                    value={campaignPageContainer.viewBy}
                                    onChange={e => campaignPageContainer.changeView(e.target.value)}
                                    options={flightOptions}
                                    placeholder="Select Flight"
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    undefined
                )
            }
            badge={
                campaign.filterByDateRange && (
                    <div style={{ marginLeft: 10 }}>
                        <InlineTooltip
                            tooltip="Only showing impressions within flight period. Some impressions may be hidden."
                            position="bottom"
                        >
                            <i className="fa fa-filter" />
                        </InlineTooltip>
                    </div>
                )
            }
            status={
                newLifecycleAndHealthIndicator ? (
                    isShowingAlert ? (
                        <CampaignIndicatorWithAlert status={campaign.lifecycle.current_state} />
                    ) : (
                        <CampaignIndicator status={campaign.lifecycle.current_state} />
                    )
                ) : (
                    <FlightIndicator position={'bottom'} label status={campaign.status} />
                )
            }
            activeTab={selectedTab ? selectedTab.value : null}
            tabs={can(PermissionsMapping.CAMPAIGN__VIEW) ? tabs : []}
            rightContent={
                <ButtonGroup className="ef5-campaign-overview-header__button-group">
                    <GlobalRefresh />
                    {can(PermissionsMapping.ADMIN_ORG__EDIT) && isInternalUser() && (
                        <div className="ef5-campaign-overview-header__share-button">
                            <CampaignSharing campaignId={campaign.id} />
                        </div>
                    )}
                </ButtonGroup>
            }
        />
    );
}
